// src/pages/ChartStudioPage.tsx
// 🔒 PHASE-1.3 UI STRUCTURE LOCK
// - activeTab is the single render authority
// - intent rendering is stable
// - no refactors or deletions before Phase-2

import React, { useState, useEffect, useRef } from "react";

import ReportOrientationPanel from "./chart/ReportOrientationPanel.tsx";
import ChartInputPanel from "../components/ChartInputPanel";
import RemedySection from "../components/RemedySection";
import PlanetHouseEffects from "../components/PlanetHouseEffects";
import DashaTimeline from "../components/DashaTimeline";
import PlanetConjunctions from "../components/PlanetConjunctions";
import YogaAnalysisTab from "../components/YogaAnalysisTab";
import AwarenessAreasTab from "../components/AwarenessAreasTab";
import AwarenessGuidance from "../components/AwarenessGuidance";
import DemoPlaceholder from "../components/DemoPlaceholder.tsx";
import UpgradeModal from "../components/upgrade/UpgradeModal.tsx";
import ExportChapterView from "./ExportChapterView";
import { exportFullReport } from "../lib/exportPdf.ts";
//import { knowledge } from "../knowledge/loadKnowledge.ts";

import PlanetIcon from "../components/PlanetIcon";

import { validateAndBuildLocation } from "../lib/locationHelpers";

import "../styles/phase1-typography.css";
import "../styles/ui-foundation.css";
import "../styles/phase3-guidance.css";
import "../styles/spacing-tokens.css";
import "../styles/exportPdf.css";
import "../App.css"

/* ------------------------------------------------------------------ */
/* Formatting helpers                                                  */
/* ---------------------------------------------------- */


const formatKarmicPhase = (result?: any) => {
  return result?.karmic_phase_status || "—";
};


const PLANET_HINDI: Record<string, string> = {
  Sun: "सूर्य",
  Moon: "चन्द्र",
  Mars: "मंगल",
  Mercury: "बुध",
  Jupiter: "गुरु",
  Venus: "शुक्र",
  Saturn: "शनि",
  Rahu: "राहु",
  Ketu: "केतु",
};


const SX_PROMO_BY_CHART_KEY = "sx_promo_by_chart_id_v1";

/* ------------------------------------------------------------------ */
/* UI helpers                                                          */
/* ------------------------------------------------------------------ */

const formatExpiry = (iso?: string) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;

    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
};
/* ------------------------------------------------------------------ */
/* FIXED Tooltip Bubble (mobile + desktop safe)                        */
/* ------------------------------------------------------------------ */

function FixedInfoTip({
  text,
  tipKey,
  openKey,
  setOpenKey,
}: {
  text: string;
  tipKey: string;
  openKey: string | null;
  setOpenKey: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const isOpen = openKey === tipKey;
  const [ready, setReady] = React.useState(false);

  const [pos, setPos] = React.useState({
    top: 0,
    left: 0,
  });

  // Compute fixed position on open
  React.useEffect(() => {
    if (!isOpen) {
      setReady(false);
      return;
    }
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const tooltipWidth = 320;
    const gap = 10;

    const spaceRight = window.innerWidth - rect.right;

    const openToRight = spaceRight >= tooltipWidth + gap;

    let left = openToRight ? rect.right + gap : rect.left - tooltipWidth - gap;

    let top = rect.top + rect.height / 2;

    left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
    top = Math.max(60, Math.min(top, window.innerHeight - 60));

    setPos({ top, left });
    setReady(true);
  }, [isOpen]);

  
  // Close on outside click/tap
  React.useEffect(() => {
    if (!isOpen) return;

    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      setOpenKey(null);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, setOpenKey]);

  return (
    <span
      ref={ref}
      onMouseEnter={() => setOpenKey(tipKey)}
      onMouseLeave={() => setOpenKey(null)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenKey((p) => (p === tipKey ? null : tipKey));
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 999,
        border: "1px solid rgba(148,163,184,0.35)",
        color: "#94a3b8",
        fontSize: 12,
        cursor: "help",
        userSelect: "none",
        flex: "0 0 auto",
      }}
    >
      ⓘ

      {isOpen && ready ? (
        <span
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: 320,
            maxWidth: "min(320px, 80vw)",
            background: "rgba(248,250,252,0.98)",
            color: "#0f172a",
            border: "1px solid rgba(148,163,184,0.35)",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 12,
            lineHeight: 1.45,
            zIndex: 999999,
            boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
            whiteSpace: "normal",
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}


/* ------------------------------------------------------------------ */
/* Main Component                                                       */
/* ------------------------------------------------------------------ */

export default function ChartStudioPage() {
    /* -------------------- Karmic Overview domain refs -------------------- */
  // (kept because the 4 domain cards use them for potential future scroll/highlight)
  const careerRef = useRef<HTMLDivElement | null>(null);
  const wealthRef = useRef<HTMLDivElement | null>(null);
  const relationshipRef = useRef<HTMLDivElement | null>(null);
  const vitalityRef = useRef<HTMLDivElement | null>(null);


    /* -------------------- Tab list (single source) -------------------- */
  const TABS = [
    { id: "karmic",       label: "Karmic Overview"       },
    { id: "remedies",     label: "Karmic Remedies"       },
    { id: "awareness",    label: "Awareness"              },
    { id: "basics",       label: "Birth Chart"           },
    { id: "traits",       label: "Personality Traits"    },
    { id: "houses",       label: "Planet in Houses"      },
    { id: "strength",     label: "Strong & Weak Planets" },
    { id: "conjunctions", label: "Planet Conjunctions"   },
    { id: "yogas",        label: "Yoga Analysis"         },
    { id: "dasha",        label: "Vimshottari Dasha"     },

    // 🚫 Phase-8 Step-2: removed domain shortcut tabs
    // (Career / Marriage / Health / Wealth)
    // These were not real tabs and only redirected to Karmic Overview cards.
    // They will return later only when real dedicated content exists.
  ];


  /* -------------------- Core state ------------------------------------ */
  // Basic form: name + country (country is optional metadata)
  const [form, setForm] = useState({ name: "", country: "India" });

  const [isInputCollapsed, setIsInputCollapsed] = useState(false);

    /* -------------------- Phase-8: Plan + Promo Unlock -------------------- */

  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState<
    | { type: "idle" }
    | { type: "loading" }
    | {
        type: "valid";
        plan: ActivePlan;
        expires_at: string;
        uses_count?: number;
        max_uses?: number;
      }
    | { type: "invalid"; message: string }
  >({ type: "idle" });
  const [promoByChartId, setPromoByChartId] = useState<Record<string, any>>({});

  /* -------------------- Header UI State -------------------- */
  const [activeHeaderPanel, setActiveHeaderPanel] = useState<
    "none" | "plan" | "unlock"
  >("none");

  const [highlightUpgrade, setHighlightUpgrade] = useState(false);

  const [expandedWhy, setExpandedWhy] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

 
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [includeAiSummary, setIncludeAiSummary] = useState(true);
  const [strengthOpenIndex, setStrengthOpenIndex] = React.useState<number | null>(null);
    
  // -------------------- Phase-8: Effective plan (chart-bound promo) --------------------
  const chartId = result?.chart_id || null;

  /* 🔒 Active promo for current chart (chart-bound entitlement) */
  const activePromoForChart =
    chartId && promoByChartId?.[chartId]
      ? promoByChartId[chartId]
      : null;

  const effectivePlan =
    activePromoForChart?.plan ||
    result?.astrology_references?.plan ||
    "STARTER";

    
  const canExport =
    effectivePlan === "GROWTH" || effectivePlan === "INSIGHT";

  // Birth details & location (USER INPUT ONLY)
  const [birthDetails, setBirthDetails] = useState<any>({
    date: "", // YYYY-MM-DD (normalized from input)
    time: "", // HH:MM (LOCAL civil time at birth place)
    placeName: "", // City / Place entered by user
    lat: "28.6139", // Default: New Delhi
    lon: "77.2090",
  });

  type CalcState = "idle" | "calculating" | "success" | "error";
  const [calcState, setCalcState] = useState<CalcState>("idle");

  const [locationMode, setLocationMode] = useState("city"); // "city" | "coords"
  const [locationErrors, setLocationErrors] = useState({
    placeName: "",
    lat: "",
    lon: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("karmic");

  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  // Mobile dropdown state
  const [mobileTabOpen, setMobileTabOpen] = useState(false);
  const mobileTabWrapRef = useRef<HTMLDivElement | null>(null);

  const activeTabLabel =
    TABS.find((t) => t.id === activeTab)?.label || "Select Tab";

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768); // ✅ Spec
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Close mobile dropdown on outside click
  useEffect(() => {
    if (!mobileTabOpen) return;

    const onDocClick = (e: MouseEvent) => {
      if (!mobileTabWrapRef.current) return;
      if (mobileTabWrapRef.current.contains(e.target as Node)) return;
      setMobileTabOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [mobileTabOpen]);

  // Close mobile dropdown on ESC
  useEffect(() => {
    if (!mobileTabOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileTabOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileTabOpen]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

 const verifyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;

    // 🔒 Promo must bind to a chart_id
    if (!result?.chart_id) {
      setPromoStatus({
        type: "invalid",
        message: "Calculate chart first, then unlock for that chart.",
      });
      return;
    }

    const chartId = result.chart_id;

    setPromoStatus({ type: "loading" });

    try {
      const res = await fetch(`${API_BASE}/promo/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          chart_id: chartId, // 🔒 REQUIRED for audit + entitlement binding
        }),
      });

      let data: any = null;

      try {
        data = await res.json();
      } catch {
        setPromoStatus({
          type: "invalid",
          message: "Unexpected server response.",
        });
        return;
      }

      // 🔎 Handle backend-declared errors first
      if (!data?.ok) {
        let message = "This promo code is invalid or expired.";

        if (data?.error === "USAGE_LIMIT_EXCEEDED") {
          message = "This promo code has reached its usage limit.";
        }

        if (data?.error === "INVALID_REQUEST") {
          message = "Invalid request. Please try again.";
        }

        setPromoStatus({
          type: "invalid",
          message,
        });
        return;
      }

      // 🔎 Validate required fields
      if (!data.plan || !data.expires_at) {
        setPromoStatus({
          type: "invalid",
          message: "This promo code is invalid or expired.",
        });
        return;
      }

      const plan = data.plan as ActivePlan;
      const expires_at = String(data.expires_at);

      const exp = new Date(expires_at).getTime();
      if (!Number.isFinite(exp) || Date.now() > exp) {
        setPromoStatus({
          type: "invalid",
          message: "This promo code is invalid or expired.",
        });
        return;
      }

      // ✅ Save promo binding per chart
      const entry = {
        code,
        plan,
        expires_at,
        uses_count:
          typeof data.uses_count === "number" ? data.uses_count : undefined,
        max_uses:
          typeof data.max_uses === "number" ? data.max_uses : undefined,
      };

      const next = {
        ...(promoByChartId || {}),
        [chartId]: entry,
      };

      setPromoByChartId(next);
      localStorage.setItem(
        SX_PROMO_BY_CHART_KEY,
        JSON.stringify(next)
      );

      // 🔔 UI-only display state (NOT entitlement source)
      setPromoStatus({
        type: "valid",
        plan,
        expires_at,
        uses_count: entry.uses_count,
        max_uses: entry.max_uses,
      });

      // 🔁 Re-run calculation so backend resolves upgraded plan
      try {

        const payload = {
          chart_id: chartId,
        };

        // If birth inputs exist in this session, send them again
        if (birthDetails?.dob && birthDetails?.time) {
          payload.name = birthDetails.name || null;
          payload.dob = birthDetails.dob;
          payload.time = birthDetails.time;
          payload.city = birthDetails.placeName || null;
          payload.country = birthDetails.country || null;
          payload.latitude = birthDetails.lat || null;
          payload.longitude = birthDetails.lon || null;
        }

        const recalc = await fetch(`${API_BASE}/calculate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const newResult = await recalc.json();

        if (newResult?.ok) {
          setResult(newResult);
        }

      } catch (err) {
        console.warn("Chart refresh failed after promo unlock", err);
      }

      // ⚠ DO NOT setActivePlan(plan)
      // Backend must resolve effective plan from chart_id
}   catch (err) {
      setPromoStatus({
        type: "invalid",
        message: "Network error. Please try again.",
      });
    }
  };

const [exportChapterKey, setExportChapterKey] = useState<string | null>(null);

async function renderChapter(key: string) {
  setExportChapterKey(key);
  await new Promise((r) => setTimeout(r, 80));
}

const runExportPdf = async () => {
  if (!result?.chart_id) return;

  if (!canExport) {
    alert("Export is locked in this plan.");
    return;
  }

  setIsExporting(true);
  setExportError("");

  try {
    await exportFullReport({
      result,
      effectivePlan,
      renderChapter,
      includeAiSummary,
    });
  } catch (e) {
    console.error(e);
    setExportError("Export failed. Try again.");
  } finally {
    setIsExporting(false);
  }
};

  /* -------------------- Reset ----------------------------------------- */

  const resetAll = () => {
    setForm({
      name: "",
      country: "India",
    });
    setBirthDetails({
      date: "",
      time: "",
      tzOffsetMinutes: 330,
      placeName: "",
      lat: "28.6139",
      lon: "77.2090",
    });
    setLocationMode("city");
    setLocationErrors({ placeName: "", lat: "", lon: "" });
    setResult(null);
    setError("");
    setCalcState("idle");
    setIsInputCollapsed(false);
    setActiveTab("karmic");
    setPromoCode("");
    setPromoStatus({ type: "idle" });
  };

  /* -------------------- Effects --------------------------------------- */
  // 🔒 Phase-2A: prevent stale coordinates when switching location mode
  useEffect(() => {
    if (locationMode === "city") {
      setBirthDetails((prev: any) => ({
        ...prev,
        lat: "",
        lon: "",
      }));
    }
  }, [locationMode]);

  // 🔒 UI hygiene: collapse "Why this?" on new chart result
  useEffect(() => {
    setExpandedWhy(null);
  }, [result?.chart_id]);

  // 🔒 UI hygiene: collapse "Why this?" when leaving karmic overview
  useEffect(() => {
    if (activeTab !== "karmic") {
      setExpandedWhy(null);
    }
  }, [activeTab]);

  // 🔒 Phase-8: Promo status must be chart-bound (reset badge on new chart)
  useEffect(() => {
    setPromoStatus({ type: "idle" });
    setPromoCode(""); // ✅ clear promo box on new chart
  }, [result?.chart_id]);



  // 🔒 Phase-8: Load promo mapping (chart-bound) from localStorage on app load
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SX_PROMO_BY_CHART_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      // Cleanup expired entries on load
      const now = Date.now();
      const next: Record<string, any> = {};

      Object.entries(parsed).forEach(([chartId, data]: any) => {
        const expires_at = String(data?.expires_at || "");
        const exp = new Date(expires_at).getTime();
        if (!expires_at || !Number.isFinite(exp)) return;
        if (now > exp) return;
        next[chartId] = data;
      });

      setPromoByChartId(next);
      localStorage.setItem(SX_PROMO_BY_CHART_KEY, JSON.stringify(next));

      // Optional: keep last typed promo code in input
      // (we keep your current behavior: do nothing)
    } catch {
      localStorage.removeItem(SX_PROMO_BY_CHART_KEY);
    }
  }, []);

// 🔒 Allow reopening a chart via chart_id in URL (used by unlock links)
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const chartIdFromUrl = params.get("chart_id");

  if (!chartIdFromUrl) return;

  const loadChart = async () => {
    try {
      setCalcState("calculating");

      const res = await fetch(`${API_BASE}/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chart_id: chartIdFromUrl,
        }),
      });

      const data = await res.json();

      if (!data?.ok) {
        setCalcState("error");
        return;
      }

      setResult({
        chart_id: data.chart_id,
        awareness_area_ids: data.awareness_area_ids || [],
        ...data,
      });

      setActiveTab("karmic");
      setCalcState("success");
      setIsInputCollapsed(true);

    } catch (err) {
      console.error("Chart load failed", err);
      setCalcState("error");
    }
  };

  loadChart();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);
  
  const scrollMobileTabsToTop = () => {
      if (!isMobile) return;
      if (!mobileTabWrapRef.current) return;

      const rect = mobileTabWrapRef.current.getBoundingClientRect();
      const y = rect.top + window.pageYOffset - 6; // tiny breathing space

      window.scrollTo({ top: y, behavior: "smooth" });
    };

  /* -------------------- Tab click handler (desktop + mobile) ---------- */
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  /* -------------------- API call -------------------------------------- */
  const callApi = async () => {
    setCalcState("calculating");
    setResult(null);
    setLoading(true);
    setError("");
    setLocationErrors({ placeName: "", lat: "", lon: "" });

    // 1) Date / time validation
    if (!birthDetails.date || !birthDetails.time) {
      setError("Please enter a valid date and time of birth.");
      setCalcState("error");
      setLoading(false);
      return;
    }

    // 2) Location validation
    const { errors: locErrors, locationPayload } = validateAndBuildLocation({
      birthDetails,
      form,
      locationMode,
    });

    if (
      locErrors.placeName ||
      locErrors.lat ||
      locErrors.lon ||
      !locationPayload
    ) {
      setLocationErrors({
        placeName: locErrors.placeName,
        lat: locErrors.lat,
        lon: locErrors.lon,
      });

      if (locErrors.topMessage) {
        setError(locErrors.topMessage);
      }

      setCalcState("error");
      setLoading(false);
      return;
    }

    try {
      // 3) Build payload
      const payload = {
        name: form.name,
        dob: birthDetails.date,
        time: birthDetails.time,
        ...locationPayload,
      };

      const res = await fetch(`${API_BASE}/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.ok) {
        setError(
          [data.error_type, data.error_message]
            .filter(Boolean)
            .join(": ") || "Something went wrong while processing the chart."
        );
        setCalcState("error");
        return;
      }

      if (!data.chart_id) {
        console.error("Missing chart_id in /calculate response", data);
        setCalcState("error");
        return;
      }

    
      // 4) Success path
      setResult({
        chart_id: data.chart_id,
        awareness_area_ids: data.awareness_area_ids || [],
        ...data,
        conjunctions: data.conjunctions,
        remedies: data.remedies,
      });

      setActiveTab("karmic"); // 🔒 Phase-6 authority
      setCalcState("success");
      setIsInputCollapsed(true);

      // Smooth scroll into report area
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 60);

      // 5) Geocoded coords (city mode only)
      if (
        locationMode === "city" &&
        typeof data.latitude === "number" &&
        typeof data.longitude === "number"
      ) {
        setBirthDetails((prev: any) => ({
          ...prev,
          lat: String(data.latitude),
          lon: String(data.longitude),
        }));
      }
    } catch (err: any) {
      setError("Network error: " + err.message);
      setCalcState("error");
    } finally {
      setLoading(false);
    }
  };

  const TabHeader = ({
    icon,
    title,
    subtitle,
  }: {
    icon?: string;
    title: string;
    subtitle?: string;
  }) => {
    return (
      <div style={{ marginBottom: 14, marginTop: 10}}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            {icon ? (
              <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>
            ) : null}

            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                letterSpacing: 0.2,
                color: "#fbbf24", // ✅ brand orange
              }}
            >
              {title}
            </div>
          </div>
        </div>

        {subtitle ? (
          <div style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6 }}>
            {subtitle}
          </div>
        ) : null}
      </div>
    );
  };

  const LockBlock = () => (
    <div
      style={{
        marginTop: 36,
        padding: 20,
        borderRadius: 14,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(148,163,184,0.15)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: "#64748b",
          marginBottom: 10,
        }}
      >
        Interpretive Depth
      </div>

      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        🔒 Detailed Planet Interpretation
      </div>

      <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7 }}>
        This section provides deeper interpretation of how each planet
        operates psychologically, how sign and house placement shape
        behavior, and awareness guidance for balancing planetary energies.

        <br /><br />

        Insight plan provides full interpretive depth across all modules.
      </div>
    </div>
  );


  const renderPersonalityTraitsFromPayload = () => {
    const pt = result?.personality_traits;

    // Fallback handling (PM-approved)
    if (!pt || !pt.sections || !Array.isArray(pt.sections)) {
      return (
        <div style={{ fontSize: 13, color: "#9ca3af" }}>
          Traits are not available for this chart yet.
        </div>
      );
    }

    return (
      <div style={{ fontSize: 13 }}>
        {/* Tab header */}
        <TabHeader
            icon="🧠"
            title={pt.ui?.tab_title || "Personality Traits"}
            subtitle={pt.ui?.how_to_read || ""}
          />


        {/* Sections */}
        <div style={{ display: "grid", gap: 12 }}>
        {pt.sections.map((section: any) => {
console.log("TRAITS SECTION:", section);
          if (!section) return null;

          // 🔒 Backend-driven DEMO section (Starter lock)
          if (section.mode !== "REAL") {
            return (
              <div key={section.id}>
                <DemoPlaceholder
                  plan={effectivePlan}
                  tabLabel={section.title || "Moon Nakshatra + Pada"}
                />
              </div>
            );
          }

          // Skip invalid fallback sections
          if (section.ok === false) return null;

          // Identify Nakshatra section for advanced blocks
          const isNak = section.id === "moon_nakshatra";
          
  const showStrengths =
    isNak &&
    Array.isArray(section.strengths) &&
    section.strengths.length > 0;

  const showShadow =
    isNak &&
    Array.isArray(section.shadow_patterns) &&
    section.shadow_patterns.length > 0;

  const showAwareness =
    isNak &&
    Array.isArray(section.karmic_awareness_guidance) &&
    section.karmic_awareness_guidance.length > 0;

  const showPada = isNak && section.pada && section.pada.number;

  return (
    <div
      key={section.id}
      style={{
        padding: 12,
        borderRadius: 10,
        background: "rgba(15,23,42,0.95)",
        border: "1px solid rgba(55,65,81,0.9)",
      }}
    >
      {/* Title */}
      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 15 }}>
        {section.icon ? `${section.icon} ` : ""}
        {section.title}
      </div>

      {/* Subtitle */}
      {section.subtitle && (
        <div
          style={{
            fontSize: 13,
            color: "#fbbf24",
            marginBottom: 10,
            fontWeight: 700,
          }}
        >
          {section.subtitle}
        </div>
      )}

      {/* Intro */}
      {section.intro && (
        <div
          style={{
            color: "#cbd5e1",
            lineHeight: 1.6,
            marginBottom: 10,
          }}
        >
          {section.intro}
        </div>
      )}

      {/* Bullets */}
      {Array.isArray(section.bullets) && section.bullets.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          {section.bullets.map((b: string, i: number) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}

      {/* Collapsible blocks ONLY for Nakshatra + Pada */}
      {isNak && (showStrengths || showShadow || showAwareness || showPada) && (
        <div style={{ marginTop: 12 }}>
                      {/* Strengths */}
                      {showStrengths && (
                        <details
                          className="sx-collapse"
                          style={{
                            marginTop: 12,
                            borderTop: "1px dashed rgba(148,163,184,0.25)",
                            paddingTop: 10,
                          }}
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                              color: "#e5e7eb",
                              fontSize: 13,
                              listStyle: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <span style={{ flex: 1, color:"#93c5fd", }}>Strengths</span>

                            <FixedInfoTip
                              tipKey={`${section.id}-strengths`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Strengths are the natural capacities this pattern supports when you are regulated and clear."
                            />
                          </summary>
                          <ul
                            style={{
                              margin: "6px 0 0 0",
                              paddingLeft: 18,
                              lineHeight: 1.7,
                            }}
                          >
                            {section.strengths.map((x: string, i: number) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        </details>
                      )}

                      {/* Shadow patterns */}
                      {showShadow && (
                        <details
                          className="sx-collapse"
                          style={{
                            marginTop: 12,
                            borderTop: "1px dashed rgba(148,163,184,0.25)",
                            paddingTop: 10,
                          }}
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                              color: "#e5e7eb",
                              fontSize: 13,
                              listStyle: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <span style={{ flex: 1, textAlign: "left", color:"#93c5fd", }}>
                              Shadow patterns
                            </span>
                            <FixedInfoTip
                              tipKey={`${section.id}-shadow`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Shadow patterns are the common ways this energy can feel distorted under stress, fear, or over-effort."
                            />
                          </summary>

                          <ul
                            style={{
                              margin: "6px 0 0 0",
                              paddingLeft: 18,
                              lineHeight: 1.7,
                            }}
                          >
                            {section.shadow_patterns.map((x: string, i: number) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        </details>
                      )}

                      {/* Karmic awareness */}
                      {showAwareness && (
                        <details
                          className="sx-collapse"
                          style={{
                            marginTop: 12,
                            borderTop: "1px dashed rgba(148,163,184,0.25)",
                            paddingTop: 10,
                          }}
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                              color: "#e5e7eb",
                              fontSize: 13,
                              listStyle: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <span style={{ flex: 1, textAlign: "left", color:"#93c5fd" }}>
                              Karmic awareness
                            </span>
                            <FixedInfoTip
                              tipKey={`${section.id}-awareness`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Karmic awareness highlights what this pattern is trying to teach through repetition — so you can respond consciously."
                            />
                          </summary>
                          <ul
                            style={{
                              margin: "6px 0 0 0",
                              paddingLeft: 18,
                              lineHeight: 1.7,
                            }}
                          >
                            {section.karmic_awareness_guidance.map(
                              (x: string, i: number) => (
                                <li key={i}>{x}</li>
                              )
                            )}
                          </ul>
                        </details>
                      )}

                      {/* Pada refinement */}
                      {showPada && (
                        <details
                          className="sx-collapse"
                          style={{
                            marginTop: 12,
                            borderTop: "1px dashed rgba(148,163,184,0.25)",
                            paddingTop: 10,
                          }}
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              fontWeight: 700,
                              color: "#e5e7eb",
                              fontSize: 13,
                              listStyle: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <span style={{ flex: 1, textAlign: "left", color:"#93c5fd" }}>
                              Pada refinement
                            </span>
                            <FixedInfoTip
                              tipKey={`${section.id}-pada`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Pada refines how the same Nakshatra expresses itself — like a sub-style of the same nature."
                            />
                          </summary>

                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontWeight: 700, marginBottom: 10 }}>
                              Pada {section.pada.number} ·{" "}
                              <span style={{ color: "#fbbf24", fontWeight: 600 }}>
                                {section.pada.tone}
                              </span>
                            </div>

                            {/* Pada traits */}
                            {Array.isArray(section.pada.traits) &&
                              section.pada.traits.length > 0 && (
                                <div style={{ marginBottom: 10 }}>
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      marginBottom: 6,
                                      fontSize: 13,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 10,
                                    }}
                                  >
                                    <span style={{ color:"#93c5fd" }}>Traits</span>

                                    <FixedInfoTip
                                      tipKey={`${section.id}-pada-traits`}
                                      openKey={openTooltipKey}
                                      setOpenKey={setOpenTooltipKey}
                                      text="Traits are broad tendencies — not fixed labels. You may relate to some more than others."
                                    />
                                  </div>

                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: 18,
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {section.pada.traits.map(
                                      (x: string, i: number) => (
                                        <li key={i}>{x}</li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}

                            {/* Pada shadow */}
                            {Array.isArray(section.pada.shadow) &&
                              section.pada.shadow.length > 0 && (
                                <div style={{ marginBottom: 10 }}>
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      marginBottom: 6,
                                      fontSize: 13,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 10,
                                    }}
                                  >
                                    <span style={{ color:"#93c5fd" }}>Shadow patterns</span>
                                    <FixedInfoTip
                                      tipKey={`${section.id}-pada-shadow`}
                                      openKey={openTooltipKey}
                                      setOpenKey={setOpenTooltipKey}
                                      text="Shadow patterns are the common ways this energy can feel distorted under stress, fear, or over-effort."
                                    />
                                  </div>

                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: 18,
                                      lineHeight: 1.7,
                                    }}
                                  >
                                    {section.pada.shadow.map(
                                      (x: string, i: number) => (
                                        <li key={i}>{x}</li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}

                            {/* Awareness hint */}
                            {section.pada.awareness_hint && (
                              <div style={{ color: "#cbd5e1", lineHeight: 1.6 }}>
                                <div
                                  style={{
                                    fontWeight: 700,
                                    marginBottom: 6,
                                    fontSize: 13,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 10,
                                  }}
                                >
                                  <span style={{ color:"#93c5fd" }}>Awareness hint</span>
                                  <FixedInfoTip
                                    tipKey={`${section.id}-pada-hint`}
                                    openKey={openTooltipKey}
                                    setOpenKey={setOpenTooltipKey}
                                    text="A simple reflection point to keep the pattern balanced in real life."
                                  />
                                </div>
                                <div>{section.pada.awareness_hint}</div>
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {pt.ui?.footer_disclaimer && (
          <div style={{ marginTop: 12, fontSize: 12, color: "#9ca3af" }}>
            {pt.ui.footer_disclaimer}
          </div>
        )}
      </div>
    );
  };



const canCollapse = calcState === "success";

function fallbackCopy(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed"; // prevent scroll jump
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("Copy failed", err);
  }

  document.body.removeChild(textarea);
}

return (
    <div
      style={{
        minHeight: "100vh",
        background: "#020617",
        color: "#f9fafb",
        padding: 20,
        maxWidth: 1200,
        margin: "16px auto",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 18 }}>
        {/* Brand */}
        <div
          className="brand"
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.4,
            marginBottom: 6,
          }}
        >
          <span>
            SysMind<span className="brand-x">X</span>
          </span>
          <span style={{ marginLeft: 0 }}>Astro</span>
        </div>

        {/* System Identity */}
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "#fbbf24",
            marginBottom: 6,
          }}
        >
          A Guided Awareness Instrument
        </div>

        {/* Philosophy Line */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 400,
            letterSpacing: 0.2,
            color: "#9ca3af",
            lineHeight: 1.6,
            maxWidth: 520,
          }}
        >
          Astrology interpreted as structured awareness — not prediction.
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-start",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
     
      {/* LEFT PANEL */}
      <div
        style={{
          flexShrink: 0,
          overflow: "hidden",
          marginTop: 6,

          // Desktop behavior
          width: !isMobile
            ? isInputCollapsed && canCollapse
              ? 0
              : 320
            : "100%",

          // Mobile behavior
          maxHeight: isMobile
            ? isInputCollapsed && canCollapse
              ? 0
              : 2000 // large enough to fit form
            : "none",

          opacity: isInputCollapsed && canCollapse ? 0 : 1,
          pointerEvents:
            isInputCollapsed && canCollapse ? "none" : "auto",

          transition: isMobile
            ? "max-height 250ms ease, opacity 150ms ease"
            : "width 200ms ease, opacity 150ms ease",

          borderRight: !isMobile
            ? "1px solid rgba(148,163,184,0.15)"
            : "none",
        }}
      >
        <ChartInputPanel
          form={form}
          birthDetails={birthDetails}
          locationMode={locationMode}
          locationErrors={locationErrors}
          result={result}
          error={error}
          loading={loading}
          onFormChange={(e: any) =>
            setForm((p) => ({ ...p, [e.target.name]: e.target.value }))
          }
          onBirthDetailsChange={setBirthDetails}
          onLocationModeChange={setLocationMode}
          onCalculate={callApi}
          onReset={resetAll}
        />
      </div>

      {/* RIGHT PANEL */}
      <div
        style={{
          width: isMobile ? "100%" : undefined,
          background: "rgba(15,23,42,0.95)",
          border: "2px solid rgba(148,163,184,0.4)",
          borderRadius: 10,
          padding: 12,
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* ===================== */}
        {/* IDLE STATE */}
        {/* ===================== */}
        {calcState === "idle" && (
          <ReportOrientationPanel />
        )}

        {/* ===================== */}
        {/* CALCULATING STATE */}
        {/* ===================== */}
        {calcState === "calculating" && (
          <div
            style={{
              padding: 28,
              color: "#94a3b8",
              fontSize: 14,
            }}
          >
            Preparing your chart…
          </div>
        )}

        {/* ===================== */}
        {/* ERROR STATE */}
        {/* ===================== */}
        {calcState === "error" && (
          <div
            style={{
              padding: 24,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Calculation could not be completed.
            </div>
            <div style={{ color: "#9ca3af", fontSize: 13 }}>
              No chart data was generated. Please review your inputs and try again.
            </div>
          </div>
        )}

        {/* ===================== */}
        {/* SUCCESS STATE */}
        {/* ===================== */}
        {calcState === "success" && result && (
          <>
            {/* TOGGLE */}
            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setIsInputCollapsed((p) => !p)}
                style={{
                  fontSize: 12,
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(148,163,184,0.4)",
                  background: "transparent",
                  color: "#e5e7eb",
                  cursor: "pointer",
                  transition: "opacity 150ms ease",
                }}
              >
                {isInputCollapsed
                  ? "← Edit Birth Details"
                  : "← Hide Input Panel"}
              </button>
            </div>
          </>
        )}
            

          {/* ================= SUCCESS STATE CONTENT ================= */}
          {calcState === "success" && result && (
            <>
              {/* ================= CHART IDENTITY HEADER ================= */}
              <div className="app-container" style={{ marginBottom: 14 }}>
                {/* Line 1 – Core Identity */}
                <div style={{ marginBottom: 4, fontSize: 14 }}>
                  <b>Name:</b> {result.received?.name || "-"} ·{" "}
                  <b>DOB:</b> {result.received?.dob} ·{" "}
                  <b>Birth Time (Local):</b> {result.received?.time} · 🕒{" "}
                  <b>Timezone Applied:</b> {result.timezone} ·{" "}
                  📍 <b>Location Used:</b>{" "}
                  {result.location_resolved || "Unknown"}
                </div>

                {/* Line 2 – Astro Identity */}
                <div
                  style={{
                    color: "#cbd5e1",
                    fontSize: 13,
                    marginBottom: 8,
                    paddingRight: ""
                  }}
                > 
                  <b>Lagna:</b> {result.lagna.sign}{" "}
                  {result.lagna.degree_in_sign.toFixed(2)}° ·{" "}
                  <b>Moon Nakshatra:</b>{" "}
                  {result.moon_nakshatra.nakshatra} (Pada{" "}
                  {result.moon_nakshatra.pada})
                
                </div>

                {/* Line 3 – Chart Key (Recovery Anchor) */}
                {result.chart_id && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      fontSize: 12,
                      color: "#94a3b8",
                      marginTop: 4
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap"
                      }}
                    >
                      <span style={{ color: "#cbd5e1" }}>
                        🔑 <b>Chart Key:</b>
                      </span>

                      <span
                        style={{
                          fontFamily: "monospace",
                          letterSpacing: "0.6px",
                          color: "#fbbf24"
                        }}
                      >
                        {result.chart_id.toUpperCase()}
                      </span>

                      <button
                        type="button"
                          onClick={() => {

                            const chartLink = `${window.location.origin}/chart?chart_id=${result.chart_id}`;

                            const summary = `
                            SysMindX Astro — Chart Record

                            Name: ${result.received?.name || "Not stored"}
                            DOB: ${result.received?.dob || "Not stored"}
                            Birth Time (Local): ${result.received?.time || "Not stored"}
                            Timezone: ${result.timezone || "Unknown"}
                            Location Used: ${result.location_resolved || "Unknown"}

                            Lagna: ${result.lagna.sign} ${result.lagna.degree_in_sign.toFixed(2)}°
                            Moon Nakshatra: ${result.moon_nakshatra.nakshatra} (Pada ${result.moon_nakshatra.pada})

                            Chart Key:
                            ${result.chart_id}

                            Open this link to instantly restore your chart:
                            ${chartLink}

                            Privacy Note:
                            SysMindX does not store your personal birth details.
                            This record helps you verify and reopen your chart anytime.
                            `;

                            if (navigator.share) {
                              navigator.share({
                                title: "SysMindX Astro — Chart Record",
                                text: summary,
                              }).catch(() => {});
                            } else if (navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(summary).catch(() => {
                                fallbackCopy(summary);
                              });
                            } else {
                              fallbackCopy(summary);
                            }

                            }}
                            style={{
                              fontSize: 11,
                              padding: "2px 10px",
                              borderRadius: 999,
                              border: "1px solid rgba(148,163,184,0.35)",
                              background: "rgba(251,191,36,0.08)",
                              color: "#fbbf24",
                              cursor: "pointer",
                              fontWeight: 600
                            }}
                          >
                            Share Chart Summary
                          </button>
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 16,
                              animation: "sxPointer 1.2s infinite",
                            }}
                          >
                          👈
                          </span>
                      

                      <div style={{ opacity: 0.7 }}>
                        Please save this record to reopen your chart later.
                      </div> 
                    </div>

                    <div style={{ fontSize: 11, color: "#64748b", maxWidth: 520 }}>
                      Privacy note: SysMindX does not store your personal birth details.
                      Charts reopened using a Chart Key restore only the calculated
                      astrological structure.
                    </div>
                  </div>
                )}
              </div>

              {/* ================= ACCESS CONTROL BAR ================= */}
              {/* Button row — Plan chip, Export, Upgrade all on one line */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                {/* PLAN CHIP */}
                <button
                  onClick={() =>
                    setActiveHeaderPanel((p) => (p === "plan" ? "none" : "plan"))
                  }
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.25)",
                    background: "rgba(15,23,42,0.6)",
                    color: effectivePlan === "STARTER" ? "#94a3b8" : "#fbbf24",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    cursor: "pointer",
                    transition: "background 0.15s ease, border 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.border = "1px solid rgba(148,163,184,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    if (isMobile) return;
                    e.currentTarget.style.border = "1px solid rgba(148,163,184,0.25)";
                  }}
                >
                  {effectivePlan}
                  <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                    {activeHeaderPanel === "plan" ? "▴" : "▾"}
                  </span>
                </button>

                {/* EXPORT BUTTON — just the button, sub-content sits below the row */}
                <button
                  onClick={() => {
                    if (!canExport) {
                      setHighlightUpgrade(true);
                      setTimeout(() => setHighlightUpgrade(false), 1200);
                      return;
                    }
                    runExportPdf();
                  }}
                  disabled={isExporting}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    border: "1px solid rgba(148,163,184,0.35)",
                    background: canExport
                      ? "linear-gradient(90deg, rgba(96,165,250,0.18), rgba(250,204,21,0.10))"
                      : "rgba(148,163,184,0.08)",
                    color: canExport ? "#f9fafb" : "#94a3b8",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: !canExport || isExporting ? "not-allowed" : "pointer",
                  }}
                  title={
                    canExport
                      ? "Download full PDF report"
                      : "Upgrade to Growth or Insight to enable PDF export"
                  }
                >
                  {isExporting
                    ? "Exporting…"
                    : canExport
                    ? "Export PDF"
                    : "🔒 Export PDF"}
                </button>

                {/* UPGRADE — Always visible except Insight */}
                {effectivePlan !== "INSIGHT" && (
                  <>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 999,
                        border: highlightUpgrade
                          ? "1px solid #fbbf24"
                          : "1px solid rgba(148,163,184,0.35)",
                        background: highlightUpgrade
                          ? "rgba(251,191,36,0.15)"
                          : "transparent",
                        color: highlightUpgrade ? "#fbbf24" : "#e5e7eb",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                      }}
                    >
                      Upgrade
                    </button>

                    {showUpgradeModal && (
                      <UpgradeModal
                        chartId={result?.chart_id}
                        onClose={() => setShowUpgradeModal(false)}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Sub-content below button row — checkbox, progress, errors */}
              {canExport && (
                <div style={{ marginBottom: 8, display: "flex", alignItems: "flex-start", gap: 7 }}>
                  <input
                    type="checkbox"
                    id="include-ai-summary"
                    checked={includeAiSummary}
                    onChange={(e) => setIncludeAiSummary(e.target.checked)}
                    disabled={isExporting}
                    style={{ marginTop: 2, accentColor: "#fbbf24", cursor: "pointer", flexShrink: 0 }}
                  />
                  <label
                    htmlFor="include-ai-summary"
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      lineHeight: 1.5,
                      cursor: isExporting ? "not-allowed" : "pointer",
                    }}
                  >
                    Include AI Summary{" "}
                    <span style={{ color: "#64748b" }}>
                      — sends anonymised chart data to Anthropic (US).{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#64748b", textDecoration: "underline" }}
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                </div>
              )}
              {isExporting && (
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: 11,
                    marginBottom: 8,
                    lineHeight: 1.5,
                  }}
                >
                  {includeAiSummary
                    ? "Generating AI summary then building PDF — this takes 15–25 seconds."
                    : "Building your PDF — this takes 10–15 seconds."}
                  <br />
                  Please don't close or refresh the page.
                </div>
              )}
              {exportError && (
                <div
                  style={{
                    color: "#f87171",
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  {exportError}
                </div>
              )}
              {/* ================= HEADER EXPAND PANELS ================= */}
              {activeHeaderPanel === "plan" && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: 14,
                    borderRadius: 12,
                    background: "rgba(2,6,23,0.85)",
                    border: "1px solid rgba(148,163,184,0.18)",
                    fontSize: 13,
                  }}
                >
                  {/* -------------------- PLAN TITLE -------------------- */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      Current Plan:{" "}
                      <span style={{ color: "#fbbf24" }}>{effectivePlan}</span>
                    </div>

                    <button
                      onClick={() => setActiveHeaderPanel("none")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        fontSize: 16,
                        padding: 0,
                      }}
                      title="Close panel"
                    >
                      ▴
                    </button>
                  </div>

                  {/* -------------------- PLAN DESCRIPTION -------------------- */}
                  {effectivePlan === "STARTER" && (
                    <div style={{ color: "#9ca3af", marginBottom: 10 }}>
                      Starter includes foundational structural layers.
                      Upgrade or unlock to access deeper interpretive layers.
                    </div>
                  )}

                  {effectivePlan === "GROWTH" && (
                    <div style={{ color: "#9ca3af", marginBottom: 10 }}>
                      Growth includes full interpretive flow and PDF export.
                    </div>
                  )}

                  {effectivePlan === "INSIGHT" && (
                    <div style={{ color: "#9ca3af", marginBottom: 10 }}>
                      Insight unlocks complete karmic and timing depth.
                    </div>
                  )}

                  {effectivePlan === "PREMIUM_UNLOCK" && (
                    <div style={{ color: "#9ca3af", marginBottom: 10 }}>
                      Preview plan shows extended layers temporarily.
                    </div>
                  )}

                  {/* ===================================================== */}
                  {/* 🔐 UNLOCK SECTION — ONLY IF STILL STARTER            */}
                  {/* ===================================================== */}
                  {(effectivePlan === "STARTER" || effectivePlan === "PREMIUM_UNLOCK") && !activePromoForChart && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 10,
                        border: "1px solid rgba(251,191,36,0.25)",
                        background: "rgba(251,191,36,0.06)",
                      }}
                    >
                      <div style={{ marginBottom: 8, fontWeight: 600 }}>
                        Have a promo code?
                      </div>

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <input
                          value={promoCode}
                          onChange={(e) => {
                            setPromoCode(e.target.value);
                            if (promoStatus.type !== "idle")
                              setPromoStatus({ type: "idle" });
                          }}
                          placeholder="Enter promo code"
                          style={{
                            flex: 1,
                            minWidth: 200,
                            padding: "8px 10px",
                            borderRadius: 10,
                            border: "1px solid rgba(148,163,184,0.25)",
                            background: "rgba(15,23,42,0.9)",
                            color: "#e5e7eb",
                          }}
                        />

                        <button
                          onClick={verifyPromoCode}
                          disabled={
                            !promoCode.trim() ||
                            promoStatus.type === "loading"
                          }
                          style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: "1px solid rgba(148,163,184,0.35)",
                            background:
                              "linear-gradient(90deg,#f97316,#fbbf24)",
                            color: "#111827",
                            fontWeight: 700,
                            cursor:
                              !promoCode.trim() ||
                              promoStatus.type === "loading"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          {promoStatus.type === "loading"
                            ? "Verifying…"
                            : "Verify"}
                        </button>
                      </div>

                      {promoStatus.type === "invalid" && (
                        <div
                          style={{
                            marginTop: 8,
                            color: "#fca5a5",
                            fontSize: 12,
                          }}
                        >
                          {promoStatus.message}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ===================================================== */}
                  {/* 🔓 ENTITLEMENT SECTION — AFTER UNLOCK                */}
                  {/* ===================================================== */}
                  {activePromoForChart && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "12px 14px",
                        borderRadius: 10,
                        background: "rgba(34,197,94,0.08)",
                        border: "1px solid rgba(34,197,94,0.25)",
                      }}
                    >
                      <div
                        style={{
                          color: "#86efac",
                          fontWeight: 600,
                          marginBottom: 6,
                        }}
                      >
                        Unlocked via Promo Code
                      </div>

                      <div style={{ color: "#9ca3af" }}>
                        Valid till:{" "}
                        <span style={{ color: "#e5e7eb" }}>
                          {formatExpiry(activePromoForChart.expires_at)}
                        </span>
                      </div>

                      {typeof activePromoForChart.max_uses === "number" && (
                        <div style={{ color: "#9ca3af", marginTop: 4 }}>
                          Usage:{" "}
                          <span style={{ color: "#e5e7eb" }}>
                            {activePromoForChart.max_uses === 0
                              ? "Unlimited"
                              : `${activePromoForChart.uses_count ?? "—"} / ${
                                  activePromoForChart.max_uses
                                }`}
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: "#64748b",
                        }}
                      >
                        Unlock applies only to this chart ID.
                        Please export before generating a new chart.
                      </div>
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 11,
                          color: "#64748b",
                          textAlign: "right",
                        }}
                      >
                        Continue browsing your chart ↓
                      </div>
                    </div>
                  )}
                </div>
              )}
                            
             {/* ================= UNIFIED TAB ROW ================= */}
            
            <div
                style={{
                  marginBottom: 12,
                  animation: "sxFadeIn 0.35s ease",
                }}
              >
              

              {/* ================= MOBILE TAB DROPDOWN (≤768px) ================= */}
              {isMobile && (
                <div
                  ref={mobileTabWrapRef}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 50,
                    isolation: "isolate",
                    transform: "translateZ(0)",
                    background: "rgba(15,23,42,0.96)",
                    border: "1px solid rgba(148,163,184,0.18)",
                    borderRadius: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setMobileTabOpen((p) => !p)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "14px 16px",
                      borderRadius: 14,
                      background: "rgba(2,6,23,0.85)",
                      border: "1px solid rgba(251,191,36,0.35)",
                      color: "#fbbf24",
                      cursor: "pointer",
                      transition: "all 180ms ease",
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                    }}
                  >
                    <div style={{ textAlign: "left" }}>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 15,
                          lineHeight: 1.2,
                        }}
                      >
                        {activeTabLabel}
                      </div>
                      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                        Tap to switch · scroll for content
                      </div>

                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        color: "#fbbf24",
                        lineHeight: 1,
                        transition: "opacity 120ms ease",
                      }}
                    >
                      {mobileTabOpen ? "▴" : "▾"}
                    </div>
                  </button>

                  {mobileTabOpen && (
                    <div
                      style={{
                        marginTop: 10,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "1px solid rgba(148,163,184,0.18)",
                        borderLeft: "3px solid #fbbf24",
                        paddingLeft: 12,
                        background: "rgba(2,6,23,0.96)",
                        boxShadow: "0 16px 40px rgba(0,0,0,0.55)",
                        maxHeight: "70vh",
                        overflowY: "auto",
                      }}
                    >
                      {TABS.map((tab) => {
                        const isActive = tab.id === activeTab;

                        return (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => {
                              const isFirstTab = tab.id === "karmic";

                              handleTabChange(tab.id);
                              setMobileTabOpen(false);

                              // 🔥 Only after first tab, bring sticky header to top
                              if (!isFirstTab) {
                                setTimeout(() => {
                                  scrollMobileTabsToTop();
                                }, 0);
                              }
                            }}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              padding: "12px 12px",
                              border: "none",
                              borderBottom:
                                "1px solid rgba(148,163,184,0.10)",
                              background: isActive
                                ? "rgba(250,204,21,0.14)"
                                : "transparent",
                              color: isActive ? "#fbbf24" : "#e5e7eb",
                              fontWeight: isActive ? 700 : 500,
                              cursor: "pointer",
                            }}
                          >
                            <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 10,
                                }}
                              >
                                <span>{tab.label}</span>

                                {isActive ? (
                                  <span
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 800,
                                      color: "#fbbf24",
                                      flexShrink: 0,
                                    }}
                                  >
                                    ✓ Active
                                  </span>
                                ) : null}
                              </div>

                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ================= DESKTOP TAB GRID ================= */}
              {!isMobile && (
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  {TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        style={{
                          padding: "6px 14px",
                          borderRadius: 999,
                          fontSize: 14,
                          cursor: "pointer",
                          border: isActive
                            ? "1px solid rgba(251,191,36,0.45)"
                            : "1px solid rgba(148,163,184,0.18)",
                          background: isActive
                            ? "rgba(251,191,36,0.10)"
                            : "rgba(15,23,42,0.4)",
                          color: isActive ? "#fbbf24" : "#cbd5e1",
                          fontWeight: isActive ? 700 : 500,
                          transition: "background 150ms ease, border 150ms ease, color 150ms ease",
                        }}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ================= TAB CONTENTS ================= */}

              {/* ================= KARMIC OVERVIEW (PHASE-6) ================= */}
              {activeTab === "karmic" && (
                <>
                  {result?.phase_6 ? (
                    <div className="card" style={{ marginTop: 8 }}>
                    <TabHeader
                      icon="🧭"
                      title="Karmic Overview"
                      subtitle="Long-term karmic patterns across life areas — read as awareness, not prediction."
                    />
                  
                  {/* ── Start Here compass ── */}
                  <div
                    style={{
                      marginBottom: 18,
                      padding: "11px 14px",
                      borderRadius: 10,
                      background: "rgba(245,158,11,0.06)",
                      border: "1px solid rgba(245,158,11,0.22)",
                      borderLeft: "3px solid rgba(245,158,11,0.70)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "rgba(245,158,11,0.70)",
                        marginBottom: 8,
                      }}
                    >
                      How to use this report
                    </div>
                    <ol
                      style={{
                        margin: 0,
                        paddingLeft: 18,
                        fontSize: 13,
                        color: "#cbd5e1",
                        lineHeight: 1.75,
                        display: "grid",
                        gap: 2,
                      }}
                    >
                      <li>
                        Read your <strong style={{ color: "#e5e7eb" }}>karmic pattern</strong> and the 4 domain cards below — this explains <em>what’s active right now</em> and why.
                      </li>
                      <li>
                        Go to <strong style={{ color: "#e5e7eb" }}>Karmic Remedies</strong> — start with the Primary practices for your active Dasha planets and build from there.
                      </li>
                      <li>
                        Check <strong style={{ color: "#e5e7eb" }}>Awareness</strong> — active Dasha signals and lifetime patterns to notice, not problems to fix.
                      </li>
                    </ol>
                    <div
                      style={{
                        marginTop: 9,
                        fontSize: 11.5,
                        color: "rgba(148,163,184,0.55)",
                      }}
                    >
                      Everything else — Personality Traits, Planet in Houses, Yogas, Dasha — is reference depth. Explore when ready.
                    </div>
                  </div>

                  {/* Intro text (KEEP tooltip title) */}
                  <div
                    style={{
                      maxWidth: 900,
                      marginBottom: 20,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: "#9ca3af",
                    }}
                    title="What you’re seeing
              This overview shows karmic patterns, not predictions.
              Read it as a reflection of tendencies, not as fixed outcomes."
                  >
                    This view reflects your long-term karmic patterns and how they are
                    shifting right now.
                    <br />
                    It does not predict events or outcomes.
                    <br />
                    Instead, it helps you notice where effort, flow, or resistance tends to
                    repeat across life areas.
                  </div>

                  {result?.karmic_overview_summary && (
                    <div
                      style={{
                        marginBottom: 18,
                        padding: "12px 14px",
                        borderRadius: 10,
                        background: "rgba(15,23,42,0.6)",
                        border: "1px solid rgba(255,255,255,0.08)"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                          marginBottom: 4,
                        }}
                      >
                        <span style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "rgba(251,191,36,0.90)",
                        }}>
                          Current Karmic Pattern
                        </span>
                        <span style={{
                          display: "inline-block",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "rgba(251,191,36,0.80)",
                          background: "rgba(251,191,36,0.08)",
                          border: "1px solid rgba(251,191,36,0.22)",
                          borderRadius: 4,
                          padding: "3px 10px",
                        }}>
                          {result?.karmic_overview_summary.pattern}
                        </span>
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          lineHeight: 1.6,
                          color: "#cbd5f5"
                        }}
                      >
                        {result?.karmic_overview_summary.description}
                      </div>
                    </div>
                  )}

                  {/* Domain grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: 12,
                      position: "relative",
                    }}
                  >
                    {/* Vertical centre divider — desktop only */}
                    {!isMobile && (
                      <div style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: "50%",
                        width: 1,
                        transform: "translateX(-50%)",
                        background: "rgba(148,163,184,0.10)",
                        pointerEvents: "none",
                      }} />
                    )}
                    {[
                      {
                        key: "career",
                        label: "Career",
                        climatePrefix: "karmic_career",
                      },
                      {
                        key: "wealth",
                        label: "Wealth",
                        climatePrefix: "karmic_financial",
                      },
                      {
                        key: "relationship",
                        label: "Relationship",
                        climatePrefix: "karmic_relationship",
                      },
                      {
                        key: "vitality",
                        label: "Vitality",
                        climatePrefix: "karmic_vitality",
                      },
                    ].map((d) => {
                      const p6 = result?.phase_6?.[d.key];
                      const kp = result?.karmic_presentation?.[d.key];
                      if (!p6 || typeof p6 !== "object") return null;

                      const climate = result?.[`${d.climatePrefix}_climate`];

                      // ✅ Domain accent (used for top strip + title)
                      const domainColor =
                        d.key === "career"
                          ? "#60a5fa"
                          : d.key === "wealth"
                          ? "#f59e0b" // SysMindX brand X orange
                          : d.key === "relationship"
                          ? "#fb7185"
                          : d.key === "vitality"
                          ? "#34d399"
                          : "#94a3b8";

                      const domainIcon =
                        d.key === "career"
                          ? "💼"
                          : d.key === "wealth"
                          ? "💰"
                          : d.key === "relationship"
                          ? "❤️"
                          : d.key === "vitality"
                          ? "🫀"
                          : "🧭";

                      return (
                        <div
                          key={d.key}
                          ref={
                            d.key === "career"
                              ? careerRef
                              : d.key === "wealth"
                              ? wealthRef
                              : d.key === "relationship"
                              ? relationshipRef
                              : d.key === "vitality"
                              ? vitalityRef
                              : undefined
                          }
                          style={{
                            padding: 14,
                            borderRadius: 10,
                            background: "rgba(15,23,42,0.95)",
                            border: `1px solid ${domainColor}23`,
                            borderTop: `1px solid ${domainColor}33`,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Domain title */}
                          <div
                            style={{
                              fontWeight: 750,
                              marginBottom: 10,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 16, lineHeight: 1 }}>
                                {domainIcon}
                              </span>

                              <span
                                style={{
                                  color: "#e2e8f0",
                                  fontSize: 16,
                                }}
                              >
                                {d.label}
                              </span>
                            </div>

                            <FixedInfoTip
                              tipKey={`karmic-${d.key}-domain`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Each area reflects how patterns tend to behave over time — not what will happen."
                            />
                          </div>

                          {/* Identity */}
                          
                            <div
                              style={{
                                fontSize: 14,
                                lineHeight: 1.6,
                                marginBottom: 6,
                              }}
                            >
                              {p6.identity_text}
                            </div>
                            
                            {kp && (
                              <div style={{ marginBottom: 4 }}>
                                <span style={{
                                  display: "inline-block",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "rgba(251,191,36,0.80)",
                                  background: "rgba(251,191,36,0.08)",
                                  border: "1px solid rgba(251,191,36,0.22)",
                                  borderRadius: 4,
                                  padding: "2px 8px",
                                }}>
                                  {kp.phase_label}
                                </span>
                              </div>
                            )}                              
                                <div> <span  style={{
                              fontSize: 12,
                              color: "#94a3b8",
                            }}>{formatKarmicPhase(result)} <FixedInfoTip
                              tipKey={`karmic-${d.key}-phase`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="Phase reflects how experience is shifting right now. It is not about timing, deadlines, or results."
                            /></span>
                              </div>
                              
                                                       
                            <div
                              style={{
                                fontSize: 13,
                                lineHeight: 1.5,
                                marginBottom: 10,
                                color: "#cbd5e1",
                              }}
                            >
                              {p6.time_text}
                            </div>
                          

                          {/* Why */}
                          
                            <button
                              onClick={() =>
                                setExpandedWhy(expandedWhy === d.key ? null : d.key)
                              }
                              title="This explains why a pattern tends to repeat,
              not the cause of a specific event."
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#94a3b8",
                                fontSize: 12,
                                cursor: "pointer",
                                padding: 0,
                                marginBottom: 6,
                              }}
                            >
                              {expandedWhy === d.key
                                ? "▼ Why this pattern?"
                                : "▶ Why this pattern?"}
                            </button>

                            {expandedWhy === d.key && (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 13,
                                  lineHeight: 1.7,
                                  color: "#e5e7eb",
                                }}
                              >
                                {p6.why_text}
                              </div>
                            )}
                          

                          {/* Guidance */}
                        
                            <div
                              style={{
                                marginTop: 14,
                                marginBottom: 8,
                                fontSize: 13,
                                lineHeight: 1.6,
                                color: "#f1f5f9",
                              }}
                            >
                              {p6.guidance}
                            </div>
                          

                          {/* Climate footer */}
                          <div
                            style={{
                              fontSize: 11.5,
                              color: "#94a3b8",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginLeft: 2,
                            }}
                          >
                            <FixedInfoTip
                              tipKey={`karmic-${d.key}-climate`}
                              openKey={openTooltipKey}
                              setOpenKey={setOpenTooltipKey}
                              text="These terms describe how movement, flow, and alignment feel right now — not outcomes or predictions."
                            />
                            <div style={{ display: "flex", gap: 10, fontWeight: 400 }}>
                              <span>Pressure: {climate?.pressure_level}</span>
                              <span>Movement: {climate?.growth_feel}</span>
                              <span>Alignment: {climate?.inner_alignment}</span>
                            </div>
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#9ca3af" }}>
                  Karmic overview data is not available for this chart.
                </div>
              )}</>
            )}

             {/* 1) Basics */}
             {activeTab === "basics" && (
                <div
                  style={{
                    fontSize: 13,
                    background: "rgba(2,6,23,0.9)",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  <TabHeader
                    icon="📜"
                    title="Birth Chart"
                    subtitle="Your birth chart snapshot — lagna, panchang, and house-wise planet placements."
                  />
                  <div style={{ marginBottom: 8 }}>
                    <b>Name:</b> {result.received?.name || "-"} ·{" "}
                    <b>DOB:</b> {result.received?.dob} ·{" "}
                    <b>Birth Time (Local):</b> {result.received?.time}
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <b>Lagna:</b> {result.lagna.sign}{" "}
                    {result.lagna.degree_in_sign.toFixed(2)}°
                    <br />
                    <b>Moon Nakshatra:</b> {result.moon_nakshatra.nakshatra} (Pada{" "}
                    {result.moon_nakshatra.pada})
                  </div>

                  {/* Chart Calculation Context */}
                  <div
                    style={{
                      marginTop: 10,
                      padding: 10,
                      borderRadius: 8,
                      background: "rgba(15,23,42,0.9)",
                      border: "1px solid rgba(55,65,81,0.9)",
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      🧭 Chart Calculation Context
                    </div>

                    {/* Location */}
                    <div style={{ marginBottom: 4 }}>
                      📍 <b>Location Used:</b>{" "}
                      {result.location_resolved || "Unknown"}
                    </div>

                    {/* Coordinates */}
                    {typeof result.latitude === "number" &&
                      typeof result.longitude === "number" && (
                        <div style={{ marginBottom: 4 }}>
                          📐 <b>Coordinates:</b>{" "}
                          {result.latitude.toFixed(4)},{" "}
                          {result.longitude.toFixed(4)}
                        </div>
                      )}

                    {/* Nearby location (display-only, coords mode) */}
                    {result.location_source === "coords" &&
                      result.location_nearby &&
                      (result.location_nearby.city ||
                        result.location_nearby.region ||
                        result.location_nearby.country) && (
                        <div style={{ marginBottom: 4 }}>
                          📌 <b>Nearest location:</b>{" "}
                          {[
                            result.location_nearby.city,
                            result.location_nearby.region,
                            result.location_nearby.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}

                    {result.location_nearby && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          marginLeft: 22,
                          marginBottom: 6,
                          fontStyle: "italic",
                        }}
                      >
                        Nearest location is shown for reference only. All
                        calculations use the exact latitude, longitude, and
                        timezone.
                      </div>
                    )}

                    {/* Timezone */}
                    {result.timezone && (
                      <div style={{ marginBottom: 4 }}>
                        🕒 <b>Timezone Applied:</b> {result.timezone}
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                      Birth time is interpreted as local civil time at the place
                      of birth. Timezone and daylight-saving rules are applied
                      automatically for accuracy.
                    </div>
                  </div>

                  {result.panchang && (
                    <div
                      style={{
                        marginBottom: 10,
                        padding: 8,
                        borderRadius: 8,
                        background: "rgba(15,23,42,0.9)",
                        border: "1px solid rgba(55,65,81,0.9)",
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        📅 Birth Panchang Snapshot
                      </div>
                      <div>Weekday: {result.panchang.weekday}</div>
                      <div>
                        Tithi: {result.panchang.paksha}{" "}
                        {result.panchang.tithi_name} (
                        {result.panchang.tithi_number})
                      </div>
                      <div>
                        Lagna Rashi: {result.panchang.lagna_rashi} (Swami:{" "}
                        {result.panchang.lagna_rashi_swami})
                      </div>
                      <div>
                        Moon Rashi: {result.panchang.moon_rashi} (Swami:{" "}
                        {result.panchang.moon_rashi_swami})
                      </div>
                      <div>
                        Nakshatra Lord: {result.panchang.moon_nakshatra_lord}
                      </div>
                    </div>
                  )}

                  {/* House grid */}
                  {result.planet_houses && (
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 4,
                          marginTop: 6,
                        }}
                      >
                        🔯 House-wise Planet Placement
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: 6,
                        }}
                      >
                        {Array.from({ length: 12 }).map((_, idx) => {
                          const hnum = idx + 1;

                          const planetsHere = Object.entries(result.planet_houses)
                            .filter(([, h]: any) => h === hnum)
                            .map(([p]) => p);

                          const signs = [
                            "Aries",
                            "Taurus",
                            "Gemini",
                            "Cancer",
                            "Leo",
                            "Virgo",
                            "Libra",
                            "Scorpio",
                            "Sagittarius",
                            "Capricorn",
                            "Aquarius",
                            "Pisces",
                          ];

                          const signHindi: Record<string, string> = {
                            Aries: "मेष",
                            Taurus: "वृषभ",
                            Gemini: "मिथुन",
                            Cancer: "कर्क",
                            Leo: "सिंह",
                            Virgo: "कन्या",
                            Libra: "तुला",
                            Scorpio: "वृश्चिक",
                            Sagittarius: "धनु",
                            Capricorn: "मकर",
                            Aquarius: "कुंभ",
                            Pisces: "मीन",
                          };

                          const lagnaSign = result.lagna.sign;
                          const lagnaIdx = signs.indexOf(lagnaSign);
                          const signForHouse = signs[(lagnaIdx + hnum - 1) % 12];
                          const hindiSign = signHindi[signForHouse];

                          const planetLabels = planetsHere.map((p: any) => {
                            const hi = PLANET_HINDI[p];
                            return hi ? `${p} (${hi})` : p;
                          });

                          return (
                            <div
                              key={hnum}
                              style={{
                                borderRadius: 6,
                                border:
                                  planetsHere.length > 0
                                    ? "2px solid rgba(251,191,36,0.9)"
                                    : "1px solid rgba(55,65,81,0.9)",
                                padding: 6,
                                fontSize: 11,
                                background: "rgba(15,23,42,0.95)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  right: 4,
                                  fontSize: 10,
                                  color: "#9ca3af",
                                }}
                              >
                                H{hnum}
                              </div>

                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#fbbf24",
                                  marginBottom: 2,
                                  fontSize: 11,
                                }}
                              >
                                House {hnum}
                              </div>

                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#93c5fd",
                                  marginBottom: 4,
                                }}
                              >
                                {signForHouse} ({hindiSign})
                              </div>

                              {planetLabels.length > 0 && (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#fef3c7",
                                    lineHeight: 1.25,
                                    textAlign: "center",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {planetLabels.join(", ")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
            )}

              {/* 2) Traits */}
              {activeTab === "traits" && (
                result?.personality_traits?.sections ? (
                  <>
                 {renderPersonalityTraitsFromPayload()}
                 {effectivePlan === "STARTER" && (
                    <DemoPlaceholder
                      plan={effectivePlan}
                      tabLabel="Moon Nakshatra + Pada"
                    />
                  )}
                  </>                
                ) : (
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>
                    Personality traits are not available for this chart.
                  </div>
                )
              )}


              {/* 3) Planet in Houses */}
              {activeTab === "houses" && (
                <div>

                  {Array.isArray(result?.planet_house_effects) ? (
                    <>
                    <PlanetHouseEffects effects={result.planet_house_effects} dasha={result.dasha} />
                    {effectivePlan === "STARTER" && <LockBlock />}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>
                      Planet house insights are not available for this chart.
                    </div>
                  )}

                </div>
              )}

              {/* 4) Strong / Weak Planets */}
              {activeTab === "strength" && (
                result?.strength_detail?.enabled === true ? (
                  <div>
                    <TabHeader
                      icon="💪"
                      title="Strong & Weak Planets"
                      subtitle="A simplified view of planetary strength based on dignity and house placement."
                    />

                    <div style={{ display: "grid", gap: 18 }}>

                      {/* Energy Overview */}
                      {(() => {
                        const distribution = result?.strength_detail?.distribution || {
                          dominant: 0,
                          balanced: 0,
                          sensitive: 0,
                        };

                        return (
                          <div className="card">
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              Energy Overview
                            </div>

                            <div style={{ fontSize: 13, color: "#9ca3af" }}>
                              You have {distribution.dominant} dominant,{" "}
                              {distribution.balanced} balanced, and{" "}
                              {distribution.sensitive} subtle planetary energies.
                              These describe relative visibility — not advantage or limitation.
                            </div>
                          </div>
                        );
                      })()}

                    {/* Planet Cards */}
                    {[...(result?.strength_detail?.insights || [])]
                      .sort((a, b) => (b.total_score || 0) - (a.total_score || 0))
                      .map((p, idx) => {

                        const details = p?.details || {};
                        const isOpen = strengthOpenIndex === idx;

                        const preview =
                          details?.planetArchetype ||
                          details?.signExpression ||
                          details?.houseExpression ||
                          "";

                        const energyWidth = Math.min(
                          100,
                          Math.max(8, p.total_score || 0)
                        );

                        return (
                          <div
                            key={idx}
                            className="card"
                            style={{
                              cursor: "pointer",
                              marginBottom: 18,
                              borderRadius: 14,
                              background:
                                isOpen
                                  ? "rgba(30,41,59,0.92)"
                                  : "rgba(30,41,59,0.82)",
                              border: isOpen
                                ? "1px solid rgba(245,158,11,0.65)"
                                : "1px solid rgba(148,163,184,0.25)",
                              transition:
                                "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                              overflow: "hidden",
                            }}
                                            onClick={() =>
                              setStrengthOpenIndex(isOpen ? null : idx)
                            }
                          >

                            {/* HEADER */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 12,
                              }}
                            >
                              <div>
                                  <div
                                      style={{
                                        marginTop: 2,
                                        fontSize: 18,
                                        opacity: 1,
                                        filter: "drop-shadow(0 0 4px rgba(0,0,0,0.4))",
                                        flex: "0 0 auto"
                                      }}
                                    >          
                                    <PlanetIcon planet={p.planet} size={18} />
                                  <span
                                    style={{
                                      fontSize: 15,
                                      color: "#f8fafc",
                                      fontWeight: 700,
                                      marginBottom: 4,
                                    }}>       
                                  {p.planet} </span>
                                </div>

                                {!isOpen && preview && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      opacity: 0.7,
                                      marginTop: 4,
                                    }}
                                  >
                                    {preview.slice(0, 120)}...
                                  </div>
                                )}

                              </div>

                              <div style={{ display: "flex", gap: 6 }}>

                                <span
                                    style={{
                                      fontSize: 14,
                                      color: "#93c5fd",
                                      fontWeight: 600,
                                      marginBottom: 4,
                                    }}> 
                                  {p.energy_type} {" & "}
                                </span>

                                {p?.dignity && (
                                  <span
                                    style={{
                                      fontSize: 14,
                                      color: "#93c5fd",
                                      fontWeight: 600,
                                      marginBottom: 4,
                                    }}> 
                                    {p.dignity}
                                  </span>
                                )}

                                <span style={{ opacity: 0.6 }}>
                                  {isOpen ? "▲" : "▼"}
                                </span>

                              </div>
                            </div>

                            {/* EXPANDED CONTENT */}
                            {isOpen && (
                              <div style={{ marginTop: 12 }}>

                                {/* ENERGY BAR */}
                                <div style={{ marginBottom: 14 }}>
                                  <div
                                    style={{
                                      height: 4,
                                      background: "rgba(255,255,255,0.08)",
                                      borderRadius: 6,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${energyWidth}%`,
                                        height: "100%",
                                        background: "#fbbf24",
                                      }}
                                    />
                                  </div>
                                </div>

                                {(details?.planetArchetype ||
                                  details?.signExpression ||
                                  details?.houseExpression) && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div className="yoga-subsection-title">
                                      Core Expression
                                    </div>

                                    {details?.planetArchetype && (
                                      <p  style={{ lineHeight: 1.55 }}>{details.planetArchetype}</p>
                                    )}

                                    {details?.signExpression && (
                                      <p  style={{ lineHeight: 1.55 }}>{details.signExpression}</p>
                                    )}

                                    {details?.houseExpression && (
                                      <p>{details.houseExpression}</p>
                                    )}
                                  </div>
                                )}

                                {details?.operationalTone && (
                                  <div style={{ marginBottom: 10 }}>
                                    <div className="yoga-subsection-title">
                                      How This Energy Operates
                                    </div>
                                    <p style={{ fontSize: 13, lineHeight: 1.55, color: "#cbd5e1" }}>{details.operationalTone}</p>
                                  </div>
                                )}

                                {Array.isArray(details?.awarenessBullets) &&
                                  details.awarenessBullets.length > 0 && (
                                    <div>
                                      <div className="yoga-subsection-title">
                                        Awareness Balance
                                      </div>

                                      <ul>
                                        {details.awarenessBullets.map((b, i) => (
                                          <li key={i} style={{ fontSize: 13, lineHeight: 1.55, color: "#cbd5e1" }}>{b}</li>
                                          
                                        ))}
                                      </ul>
                                    </div>
                                )}

                              </div>
                            )}

                          </div>
                        );
                    })}
                    {/* SUMMARY MODE */}
                    {effectivePlan !== "INSIGHT" && <LockBlock />}
                  </div>
                </div>
                ) : (
                  <DemoPlaceholder
                    plan={effectivePlan}
                    tabLabel="Strong & Weak Planets"
                  />
                )
              )}

              {/* 5) Dasha Timeline */}
              {activeTab === "dasha" && (
                result?.dasha ? (
                  <div>
                    <TabHeader
                      icon="⏳"
                      title="Vimshottari Dasha"
                      subtitle="A timing lens — showing long-term (MD), current theme (AD), and short-term intensity (PD)."
                    />

                    <DashaTimeline
                      key={result.chart_id}
                      dasha={result.dasha}
                    />
                  </div>
                ) : (
                  <DemoPlaceholder
                    plan={effectivePlan}
                    tabLabel="Vimshottari Dasha"
                  />
                )
              )}

              {/* 6) Conjunctions */}
              {activeTab === "conjunctions" && (
                result?.conjunctions?.enabled ? (
                    <div>
                      <TabHeader
                        icon="🔗"
                        title="Planet Conjunctions"
                        subtitle="Conjunctions reveal how planetary energies combine and shape recurring life patterns."
                      />

                      <PlanetConjunctions
                        conjunctions={result.conjunctions}
                        mode={result.conjunctions.mode}
                      />
                    </div>
                ) : (
                    <DemoPlaceholder
                      plan={effectivePlan}
                      tabLabel="Planet Conjunctions"
                    />
                )
              )}

              {/* 7) Remedies */}
              {activeTab === "remedies" && (
                result?.remedies ? (
                  <RemedySection
                    remedies={result.remedies}
                    dasha={result.dasha}
                    plan={effectivePlan}
                  />
                ) : (
                  <DemoPlaceholder
                    plan={effectivePlan}
                    tabLabel="Karmic Remedies"
                  />
                )
              )}

              {/* 8) Yogas */}
              {activeTab === "yogas" && (
                result?.yoga_analysis?.enabled ? (
                  <div>
                    <TabHeader
                      icon="🧘"
                      title="Yoga Analysis"
                      subtitle="Key yogas in the chart — interpreted as tendencies, not predictions."
                    />

                    <YogaAnalysisTab
                      yogaAnalysis={result.yoga_analysis}
                      mode={result.yoga_analysis.mode}
                    />
                  </div>
                ) : (
                  <DemoPlaceholder
                    plan={effectivePlan}
                    tabLabel="Yoga Analysis"
                  />
                )
              )}

              {/* 9) Awareness — two lenses: Dasha signals + Chart structure */}
              {activeTab === "awareness" && (
                <div>
                  <TabHeader
                    icon="🌿"
                    title="Awareness"
                    subtitle="Two lenses for self-observation — what your current Dasha period is calling attention to, and what your chart structure has always carried."
                  />

                  {/* ── Section 1: Awareness Guidance (Dasha-based, timing) ── */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: "rgba(245,158,11,0.80)",
                      marginBottom: 5,
                    }}>
                      This Period — What Your Dasha Is Calling Attention To
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: "#9ca3af",
                      lineHeight: 1.65,
                      marginBottom: 14,
                    }}>
                      These themes are drawn from your active Dasha planets — not from your
                      birth chart structure. They shift as your Dasha period advances.
                      Sit with them as gentle prompts, not as tasks.
                    </div>

                    {/* Starter plan — signals locked */}
                    {effectivePlan === "STARTER" ? (
                      <div style={{
                        padding: "16px 20px",
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(148,163,184,0.15)",
                      }}>
                        <div style={{
                          fontSize: 11,
                          letterSpacing: 0.8,
                          textTransform: "uppercase",
                          color: "#64748b",
                          marginBottom: 8,
                        }}>
                          Locked Section
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          🔒 Active Signals
                        </div>
                        <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7 }}>
                          Your Dasha-based awareness signals are calculated but not visible in this plan.
                          Upgrade to Growth or higher to see which patterns your active planets are
                          surfacing right now.
                        </div>
                      </div>
                    ) : (
                      <AwarenessGuidance signals={result?.awareness_signals || []} />
                    )}
                  </div>

                  {/* ── Divider ── */}
                  <div style={{
                    borderTop: "1px solid rgba(148,163,184,0.12)",
                    margin: "28px 0 24px",
                  }} />

                  {/* ── Section 2: Awareness (Dasha signals + lifetime chart patterns) ── */}
                  <div>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: "rgba(148,163,184,0.65)",
                      marginBottom: 5,
                    }}>
                      Lifetime Patterns — What Your Chart Has Always Carried
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: "#9ca3af",
                      lineHeight: 1.65,
                      marginBottom: 16,
                    }}>
                      These patterns are drawn from your birth chart structure — present
                      throughout life, independent of Dasha timing. In classical astrology
                      they are sometimes called dosha. Here they are held simply as
                      tendencies to understand — not conditions to correct.
                    </div>

                    {result?.awareness?.enabled ? (
                      <AwarenessAreasTab
                        awarenessAreaIds={
                          Array.isArray(result?.awareness?.awareness_area_ids)
                            ? result.awareness.awareness_area_ids
                            : []
                        }
                        isMobile={isMobile}
                      />
                    ) : (
                      <DemoPlaceholder
                        plan={effectivePlan}
                        tabLabel="Awareness"
                      />
                    )}
                  </div>
                </div>
              )}             
             {/* Hidden export DOM — kept at (0,0) so browser loads fonts correctly.
                  Clipped to 0×0 visually; html2canvas ignores parent overflow
                  when capturing a specific element, so the full 794px canvas
                  is still captured faithfully.
                  NOTE: do NOT use left:-99999px — Chromium skips font metrics
                  for elements far outside the viewport, causing letter-spacing
                  artifacts and page-count explosion in the generated PDF. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: -1,
              }}
            >
              <div id="sx-export-chapter">
                {exportChapterKey && (
                  <ExportChapterView
                    chapterKey={exportChapterKey}
                    result={result}
                    effectivePlan={effectivePlan}
                  />
                )}
              </div>
          </div>  
        </div>
        
      </>
        )}    
      </div> 
      </div>     
   </div>
  );
}